import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { Container, Button } from "react-bootstrap";
import UsersTableFilters from "./UsersTableFilters";
import Loader from "../../components/loader/Loader";
import UsersModal from "./UsersModal";
import InviteUserModal from "./InviteUserModal";
import "../common/tableStyles.css";
import { LuSearch, LuTrash2, LuPen } from "react-icons/lu";
import {
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/system";

import { handleUserDetails } from "../../services/users";

import Chip from "@mui/material/Chip";

import { theme, useStyles } from "../common/styles";

function UsersTableWrapper() {
  const [loading, setLoading] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowSelection, setRowSelection] = useState([]);
  const [selectedRowArr, setSelectedRowArr] = useState([]);
  const [selectedRowName, setSelectedRowName] = useState([]);
  const [editModalStatus, setEditModalStatus] = useState(false);
  const [deleteModalStatus, setDeleteModalStatus] = useState(false);
  const [inviteModalStatus, setInviteModalStatus] = useState(false);
  const [responseMessage, setResponseMessage] = useState();
  const [confirmModal, setConfirmModal] = useState(false);
  const [modalInfo, setModalInfo] = useState(false);
  const navigate = useNavigate();
  const [selectedFilterValue, setSelectedFilterValue] = useState("");
  const [roles, setRoles] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState({});

  // Status filter state for showing/hiding deactivated users
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // Reactivation modal state
  const [reactivateModalStatus, setReactivateModalStatus] = useState(false);
  const pageSize = 25;

  const [pagination, setPagination] = useState({
    pageIndex: 1,
  });

  const StyledPagination = styled(Pagination)({
    className: "myStyledPagination",
    display: "flex",
    position: "relative",
    marginTop: "-40px",
    zIndex: "999",
    justifyContent: "center",
    width: "100%",
    color: "#475467 !important",
    "& .MuiPagination-ul": {
      width: "100%",
      justifyContent: "center",
      padding: "0 16px",
      backgroundColor: "#red",
    },
    "& .MuiPagination-ul li": {
      color: "#475467",
    },
    "& .MuiPagination-ul li:last-child, & .MuiPagination-ul li:first-child": {
      fontWeight: "600 !important",
    },
    "& .MuiPagination-ul li:last-child": {
      marginLeft: "auto",
    },
    "& .MuiPagination-ul li:last-child button::before": {
      content: "'Next'",
      marginRight: "8px",
    },
    "& .MuiPagination-ul li:first-child": {
      marginRight: "auto",
    },
    "& .MuiPagination-ul li:first-child button::after": {
      content: "'Previous'",
      marginLeft: "8px",
    },
    "& .MuiPaginationItem-root": {
      fontFamily: "Inter",
      fontSize: "14px !important",
      lineHeight: "20px",
      fontWeight: "500",
      boxShadow: "none",
    },
    "& .MuiPaginationItem-page": {
      height: "40px",
      width: "40px",
    },
    "& .MuiPaginationItem-page:hover": {
      backgroundColor: "#ECFDF3",
    },
    "& .MuiPaginationItem-page.Mui-selected": {
      backgroundColor: "#FFF",
      color: "#099250",
      border: "1px solid #CDD5DF",
      borderRadius: "8px",
    },
    "& .MuiPaginationItem-ellipsis": {
      paddingTop: "11px",
    },
  });

  const classes = useStyles();

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    getUsersData(1);
  }, [searchTerm, selectedFilterValue, statusFilter]);

  const fetchRoles = async () => {
    try {
      const rolesData = await handleUserDetails.getRoles();
      // Remove duplicates based on role_name
      const uniqueRoles = rolesData.filter(
        (role, index, self) =>
          index === self.findIndex((r) => r.role_name === role.role_name),
      );
      setRoles(uniqueRoles);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleUserClickEdit = (rowData) => {
    setSelectedUser(rowData);
    setEditModalStatus(true);
    setModalInfo(true);
  };

  const handleUserClickDelete = (rowData) => {
    setSelectedUser(rowData);
    setDeleteModalStatus(true);
    setModalInfo(true);
  };

  const handleUserClickReactivate = (rowData) => {
    setSelectedUser(rowData);
    setReactivateModalStatus(true);
    setModalInfo(true);
  };

  const handleClose = () => {
    setSelectedUser({});
    setEditModalStatus(false);
    setDeleteModalStatus(false);
    setInviteModalStatus(false);
    setReactivateModalStatus(false);
    handleReset();
  };

  const handleReset = () => {
    setSelectedFilterValue("");
    setShowDeactivated(false);
    setStatusFilter("all");
    setSearchTerm("");
    setRowSelection([]);
    setSelectedRowArr([]);
  };

  const handleFilterSelect = (roleId) => {
    setSelectedFilterValue(roleId);
  };

  const handleStatusFilterChange = (event) => {
    const newStatusFilter = event.target.value;
    setStatusFilter(newStatusFilter);
    // When showing deactivated or all, we need to include deactivated users
    const includeDeactivated = newStatusFilter !== "active";
    setShowDeactivated(includeDeactivated);
  };
  const CustomTableToolbar = () => {
    // Custom Table Headers Options
    return (
      <div className="d-flex justify-content-end w-100">
        <div
          className="table-header search-filter-gap"
          style={{ display: "flex", gap: "12px", alignItems: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              displayEmpty
              disabled={loading}
              sx={{
                fontFamily: "Inter",
                fontSize: "14px",
                "& .MuiSelect-select": {
                  padding: "8px 14px",
                },
              }}
            >
              <MenuItem value="active">Active Users</MenuItem>
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="deactivated">Deactivated Only</MenuItem>
            </Select>
          </FormControl>
          <UsersTableFilters
            handleFilterSelect={handleFilterSelect}
            selectedFilterValue={selectedFilterValue}
            loading={loading}
            roles={roles}
          />
        </div>
      </div>
    );
  };

  // Table Headers
  const userColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "User Name",
        size: 400,
        minSize: 300,
        grow: true,
        Cell: ({ cell }) => {
          const rowData = cell.row.original;
          const isDeactivated =
            rowData.status === "deactivated" || rowData.is_active === false;
          return (
            <div>
              <span>{rowData.name}</span>
              {isDeactivated && rowData.email && (
                <span
                  style={{
                    fontStyle: "italic",
                    color: "#6b7280",
                    marginLeft: "8px",
                  }}
                >
                  {rowData.email}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "role_name",
        header: "Role",
        size: 15,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <Chip
              label={value || " - "}
              size="small"
              style={{
                backgroundColor: "#FFFFFF",
                color: value === "Lawyer" ? "#6941C6" : "#175CD3",
                margin: "0px",
              }}
            />
          );
        },
      },
      {
        accessorKey: "user_company",
        company: "user_company",
        header: "Law Firm",
        size: "auto",
        fontSize: "10px",
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 15,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const isActive = value === "active";
          return (
            <Chip
              label={isActive ? "Active" : "Deactivated"}
              size="small"
              style={{
                backgroundColor: isActive ? "#ECFDF3" : "#FEF3F2",
                color: isActive ? "#099250" : "#B42318",
              }}
            />
          );
        },
      },
      {
        accessorKey: "id",
        id: "id",
        size: 5,
        align: "right",
        Cell: ({ cell }) => {
          const rowData = cell.row.original;
          const isDeactivated =
            rowData.status === "deactivated" || rowData.is_active === false;

          if (isDeactivated) {
            return (
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleUserClickReactivate(rowData)}
                style={{
                  fontSize: "12px",
                  padding: "4px 12px",
                  borderColor: "#099250",
                  color: "#099250",
                }}
              >
                Reactivate
              </Button>
            );
          }

          return (
            <>
              <Button
                variant={rowData.role_name === "Admin" ? "link" : ""}
                size="sm"
                onClick={() => handleUserClickDelete(rowData)}
                disabled={rowData.role_name === "Admin"}
              >
                <LuTrash2 />
              </Button>
              <Button
                variant={rowData.role_name === "Admin" ? "link" : ""}
                className="admin-row-btn"
                size="sm"
                onClick={() => handleUserClickEdit(rowData)}
                disabled={rowData.role_name === "Admin"}
              >
                <LuPen />
              </Button>
            </>
          );
        },
      },
    ],
    [],
  );

  // Get Users Data
  const getUsersData = async (page) => {
    setLoading(true);
    try {
      const currentOffset = ((page || pagination.pageIndex) - 1) * pageSize;

      const response = await handleUserDetails.getUsers(
        selectedFilterValue,
        currentOffset,
        pageSize,
        statusFilter === "all",
        statusFilter,
        searchTerm,
      );
      setUsersData(response.users_data);
      setTotalRecords(response.pagination.total);
      setPagination({pageIndex: page || pagination?.pageIndex})
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
      throw error;
    }
  };

  // Fix the page change handler
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: value,
    }));
    getUsersData(value)
  };

  const customTable = useMaterialReactTable({
    columns: userColumns,
    data: usersData,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowSelection: false,
    enableSorting: false,
    enablePagination: false,
    getRowId: (row) => row.id,
    muiTablePaperProps: {
      sx: () => ({
        borderRadius: "12px",
        boxShadow: "none",
        "& .MuiBox-root": {
          boxShadow: "none",
          alignItems: "center",
        },
      }),
    },
    initialState: {
      showGlobalFilter: true,
    },
    muiPaginationProps: {
      shape: "rounded",
      showRowsPerPage: false,
      showFirstButton: false,
      showLastButton: false,
      fontWeight: "600",
      sx: {
        color: "#475467",
        fontSize: "14px",
        fontFamily: "Inter",
        lineHeight: "20px",
        fontWeight: "500",
        "& .MuiPagination-ul li:last-child": {
          fontWeight: "600 !important",
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search",
      className: classes.searchField,
      inputProps: {
        onChange: (e) => setSearchTerm(e.target.value),
        value: searchTerm,
        sx: {
          fontFamily: "Inter",
          fontSize: "16px",
          fontWeight: "400",
          border: "none",
        },
      },
      sx: () => ({
        minWidth: "252px",
        ".MuiInputBase-adornedStart": {
          padding: "0px 6px",
          background: "#fff",
        },
        button: {
          padding: "0px",
          width: "auto",
        },
        "& input": {
          padding: "8px 0",
          fontSize: "15px",
        },
        "&$focused": {
          borderColor: "#099250 !important",
          borderWidth: "1px !important",
          boxShadow: "none !important",
        },
      }),
      variant: "outlined",
      InputProps: {
        startAdornment: (
          <LuSearch
            style={{
              color: "#9AA4B2",
              fontSize: "20px",
              marginRight: "10px",
            }}
          />
        ),
        endAdornment: ''
      },
    },
    muiTableHeadCellProps: {
      sx: () => ({
        display: "flex",
        flex: "1 1 0",
        fontFamily: "Inter",
        textTransform: "uppercase",
        color: "#9AA4B2",
        fontSize: "10px",
        lineHeight: "19px",
        fontWeight: "700",
        padding: "6px 16px",
        borderBottom: "none",
      }),
    },
    muiTableHeadRowProps: {
      sx: () => ({
        display: "flex",
        flexDirection: "row",
        flex: "1 1 0",
        height: "52px",
        boxShadow: "none !important",
      }),
    },
    muiTopToolbarProps: {
      sx: () => ({
        "& .MuiBox-root": {
          flexDirection: "row-reverse",
          justifyContent: "space-between",
          padding: "3px !important",
        },
        "& input: focus": {
          borderColor: "#099250 !important",
          borderWidth: "1px !important",
          boxShadow: "none !important",
        },
      }),
    },
    muiTableBodyProps: {
      sx: () => ({
        "& tr": {
          display: "flex",
          flex: "1 1 0",
          flexDirection: "row",
          width: "100%",
          height: "52px",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          margin: "8px 0px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          boxShadow: "0px 1px 2px 0px #1018280F",
          boxShadow: "0px 1px 3px 0px #1018281A",
        },
        "& td": {
          flex: "1 1 0",
          display: "flex", // Make the cell a flex container
          alignItems: "center", // Center content vertically
          padding: "6px 16px", // Add padding inside cells
          whiteSpace: "nowrap", // Prevent text from wrapping
          overflow: "hidden", // Hide overflow
          borderBottom: "none", // Remove bottom border between cells
          fontFamily: "Inter",
        },
      }),
    },
    muiTableBodyRowProps: ({ row }) => {
      const isDeactivated = row.original.status === "deactivated";
      return {
        onClick: () => {},
        sx: isDeactivated
          ? {
              opacity: 0.6,
              backgroundColor: "#f9fafb !important",
            }
          : {},
      };
    },
    renderTopToolbarCustomActions: () => <CustomTableToolbar />,
    state: {
      isLoading: loading,
      globalFilter: searchTerm,
    },
  });

  return (
    <>
      {/* <Loader open={loading} /> */}
      <Container>
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="m-0 p-0">Users</h4>
          <Button
            className="btn btn-success"
            onClick={() => setInviteModalStatus(true)}
            sx={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
            disabled={loading}
          >
            Invite User
          </Button>
        </div>
        {selectedUser && Object.keys(selectedUser).length > 0 && (
          <UsersModal
            handleClose={handleClose}
            editModalStatus={editModalStatus}
            deleteModalStatus={deleteModalStatus}
            reactivateModalStatus={reactivateModalStatus}
            modalInfo={modalInfo}
            userData={selectedUser}
            onUserReactivated={() => getUsersData()}
          />
        )}
        <InviteUserModal
          show={inviteModalStatus}
          handleClose={handleClose}
          onUserInvited={getUsersData}
        />
        <div className="tab-content" id="users-tab">
          <div>
            <MaterialReactTable table={customTable} />
          </div>
          {!loading && (
            <StyledPagination
              page={pagination.pageIndex}
              count={Math.ceil(totalRecords / pageSize)}
              onChange={handlePageChange}
            />
          )}
        </div>
      </Container>
    </>
  );
}

export default UsersTableWrapper;
